import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Eye } from 'lucide-react';
import { useTrendingArticles } from '@/hooks/useArticleViews';
import { useBlogPosts, type BlogPost } from '@/hooks/useBlogPosts';
import { Skeleton } from '@/components/ui/skeleton';

interface TrendingArticlesProps {
  onArticleClick: (post: BlogPost) => void;
}

export const TrendingArticles = ({ onArticleClick }: TrendingArticlesProps) => {
  const { data: trendingData, isLoading: trendingLoading } = useTrendingArticles(7);
  const { data: allPosts = [] } = useBlogPosts();

  if (trendingLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Trending This Week
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!trendingData || trendingData.length === 0) {
    return null;
  }

  // Match trending article IDs with full post data
  const trendingPosts = trendingData
    .map(trending => {
      const post = allPosts.find(p => p.id === trending.article_id);
      return post ? { ...post, viewCount: Number(trending.view_count) } : null;
    })
    .filter(Boolean)
    .slice(0, 5);

  if (trendingPosts.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Trending This Week
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {trendingPosts.map((post, index) => (
          <div
            key={post.id}
            onClick={() => onArticleClick(post)}
            className="flex gap-3 p-3 rounded-lg hover:bg-accent cursor-pointer transition-colors group"
          >
            <div className="flex-shrink-0">
              <Badge variant="secondary" className="h-8 w-8 rounded-full flex items-center justify-center font-bold">
                {index + 1}
              </Badge>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                {post.title}
              </h4>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <Eye className="h-3 w-3" />
                <span>{post.viewCount.toLocaleString()} views</span>
                <span>•</span>
                <Badge variant="outline" className="text-xs">
                  {post.category}
                </Badge>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
